import { useApolloClient } from 'react-apollo';
import ApolloClientType from 'apollo-client';
import { SurveyVersionQuery, SurveyVersionQueryVariables } from '~lib/generatedGqlTypes';
import getSurveyVersionQuery from './SurveyVersion.graphql';
import { useAppDispatch, useAppSelector } from '~app/hooks';
import { addError } from '../errors/errorsSlice';
import { ErrorType } from '../errors';
import { selectSurvey, selectClientToken } from '../surveySlice';
import { CurrentSessionSurvey } from '../types';

/**
 * Query the survey version and check if it has changed
 * @returns {boolean} true if the survey version has changed, false otherwise
 */
export const verifySurveyVersion = async (
  apolloClient: ApolloClientType<object>,
  survey: CurrentSessionSurvey | undefined,
  clientToken: string | undefined
): Promise<boolean> => {
  try {
    const {
      data: { surveyVersion },
    } = await apolloClient.query<SurveyVersionQuery, SurveyVersionQueryVariables>({
      query: getSurveyVersionQuery,
      variables: {
        surveyId: survey?.id ?? '',
      },
      context: {
        headers: {
          'x-client-token': clientToken,
        },
      },

      // prevent cached survey version from being used
      fetchPolicy: 'network-only',
    });

    if (surveyVersion !== survey?.version) {
      return true;
    }
    return false;
  } catch (error: unknown) {
    console.error(error);
  }
  return false;
};

/**
 * Hook that returns a function which will query the current survey version and apply
 * version changed errors if necessary
 */
export const useVerifySurveyVersion = (): (() => Promise<void>) => {
  const survey = useAppSelector(selectSurvey);
  const clientToken = useAppSelector(selectClientToken);
  const dispatch = useAppDispatch();

  /** Reference to the main apolloClient */
  const apolloClient = useApolloClient();

  const verify = async (): Promise<void> => {
    const versionChanged = await verifySurveyVersion(apolloClient, survey, clientToken);
    if (versionChanged) {
      dispatch(addError({ code: ErrorType.INVALID_SURVEY_VERSION, detail: '', questionId: null }));
    }
  };

  return verify;
};
